// Non-weather hazard types (armed conflict, civil unrest, political/trade
// disruption, and operational disruptions like fires/equipment failures)
// share one visual treatment on the map -- one icon, one color -- and their
// own independent visibility toggle, separate from the weather layer.
export const GEOPOLITICAL_HAZARD_TYPES = new Set([
  'Civil Unrest',
  'Armed Conflict',
  'Political and Trade Disruption',
  'Operational Disruption',
]);

export const normalizeHazardType = (type?: string | null): string => {
  if (!type) return '';
  const t = String(type).trim();
  const lower = t.toLowerCase();
  if (lower === 'sever thunderstorm') return 'Severe Thunderstorm';
  if (lower === 'flood' || lower === 'flood warning') return 'Flood Warning';
  if (lower === 'extreme heat') return 'Extreme Heat';
  return t;
};

export const isGeopoliticalHazardType = (rawType?: string | null): boolean =>
  GEOPOLITICAL_HAZARD_TYPES.has(normalizeHazardType(rawType));

export type HazardLike = {
  hazard_type?: string | null;
  actual_effective_date?: string | null;
  actual_expiration_date?: string | null;
  geometry?: unknown;
};

type NewsflashLike = {
  polygons?: HazardLike[] | null;
};

type HazardCoordinate = {
  lat: number;
  lng: number;
};

const DEFAULT_HAZARD_DURATION_MS = 72 * 60 * 60 * 1000;

const parseHazardDate = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

export const getHazardTimeWindow = (
  hazard: HazardLike
): { startMs: number | null; endMs: number | null } => {
  const startMs = parseHazardDate(hazard.actual_effective_date);
  const explicitEndMs = parseHazardDate(hazard.actual_expiration_date);
  return {
    startMs,
    endMs:
      explicitEndMs ??
      (startMs == null ? null : startMs + DEFAULT_HAZARD_DURATION_MS),
  };
};

export const isHazardActiveAtTime = (
  hazard: HazardLike,
  atMs = Date.now()
): boolean => {
  const { startMs, endMs } = getHazardTimeWindow(hazard);
  if (startMs != null && atMs < startMs) return false;
  if (endMs != null && atMs >= endMs) return false;
  return true;
};

export const getNextHazardBoundaryMs = (
  newsflash: NewsflashLike[] | null | undefined,
  now = Date.now()
): number | null => {
  let nextBoundary: number | null = null;
  for (const item of newsflash ?? []) {
    for (const hazard of item.polygons ?? []) {
      const { startMs, endMs } = getHazardTimeWindow(hazard);
      for (const boundary of [startMs, endMs]) {
        if (boundary != null && boundary > now) {
          nextBoundary =
            nextBoundary == null ? boundary : Math.min(nextBoundary, boundary);
        }
      }
    }
  }
  return nextBoundary;
};

export const filterHazardsByVisibility = <T extends HazardLike>(
  hazards: T[],
  {
    showWeatherLayer,
    showGeopoliticalLayer,
  }: {
    showWeatherLayer: boolean;
    showGeopoliticalLayer: boolean;
  }
): T[] =>
  hazards.filter((hazard) =>
    isGeopoliticalHazardType(hazard.hazard_type)
      ? showGeopoliticalLayer
      : showWeatherLayer
  );

const isValidCoordinate = (value: unknown): value is HazardCoordinate => {
  if (!value || typeof value !== 'object') return false;
  const { lat, lng } = value as Partial<HazardCoordinate>;
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  );
};

const hasNonZeroArea = (ring: HazardCoordinate[]): boolean => {
  if (ring.length < 3) return false;

  // Unwrap longitudes so an antimeridian-crossing ring is measured locally.
  const unwrapped: HazardCoordinate[] = [];
  for (const point of ring) {
    if (unwrapped.length === 0) {
      unwrapped.push(point);
      continue;
    }
    const previousLng = unwrapped[unwrapped.length - 1].lng;
    let lng = point.lng;
    while (lng - previousLng > 180) lng -= 360;
    while (lng - previousLng < -180) lng += 360;
    unwrapped.push({ ...point, lng });
  }

  const twiceArea = unwrapped.reduce((sum, point, index) => {
    const next = unwrapped[(index + 1) % unwrapped.length];
    return sum + point.lng * next.lat - next.lng * point.lat;
  }, 0);
  return Math.abs(twiceArea) > 1e-12;
};

export const getRenderableHazardRings = (
  geometry: unknown
): HazardCoordinate[][] => {
  if (!Array.isArray(geometry)) return [];

  return geometry.flatMap((candidate) => {
    if (!Array.isArray(candidate) || !candidate.every(isValidCoordinate)) {
      return [];
    }

    const ring = [...candidate];
    const first = ring[0];
    const last = ring.at(-1);
    if (first && last && first.lat === last.lat && first.lng === last.lng) {
      ring.pop();
    }

    const uniquePositions = new Set(
      ring.map(({ lat, lng }) => `${lat}:${lng}`)
    );
    return ring.length >= 3 && uniquePositions.size >= 3 && hasNonZeroArea(ring)
      ? [[...ring, ring[0]]]
      : [];
  });
};

export const getHazardRingCenter = (
  geometry: unknown
): HazardCoordinate | null => {
  const rings = getRenderableHazardRings([geometry]);
  if (rings.length === 0) return null;

  const openRing = rings[0].slice(0, -1);
  const unwrappedLongitudes: number[] = [];
  for (const point of openRing) {
    if (unwrappedLongitudes.length === 0) {
      unwrappedLongitudes.push(point.lng);
      continue;
    }
    const previousLng = unwrappedLongitudes[unwrappedLongitudes.length - 1];
    let lng = point.lng;
    while (lng - previousLng > 180) lng -= 360;
    while (lng - previousLng < -180) lng += 360;
    unwrappedLongitudes.push(lng);
  }

  const lat =
    openRing.reduce((sum, point) => sum + point.lat, 0) / openRing.length;
  const unwrappedLng =
    unwrappedLongitudes.reduce((sum, lng) => sum + lng, 0) /
    unwrappedLongitudes.length;
  const lng = ((((unwrappedLng + 180) % 360) + 360) % 360) - 180;
  return { lat, lng };
};

export const withRenderableHazardGeometry = <T extends HazardLike>(
  hazard: T
): (T & { geometry: HazardCoordinate[][] }) | null => {
  const geometry = getRenderableHazardRings(hazard.geometry);
  return geometry.length > 0 ? { ...hazard, geometry } : null;
};

export const isActiveGeopoliticalHazard = (
  hazard: HazardLike,
  now = Date.now()
): boolean => {
  if (!isGeopoliticalHazardType(hazard.hazard_type)) return false;
  if (getRenderableHazardRings(hazard.geometry).length === 0) return false;

  return isHazardActiveAtTime(hazard, now);
};

export const hasActiveGeopoliticalAlerts = (
  newsflash: NewsflashLike[] | null | undefined,
  now = Date.now()
): boolean =>
  (newsflash ?? []).some((item) =>
    (item.polygons ?? []).some((polygon) =>
      isActiveGeopoliticalHazard(polygon, now)
    )
  );
